import {useParams} from "react-router-dom";
import React, {useEffect} from "react";
import {Button, Flex, Tooltip} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAngleDown,
    faAngleRight,
    faAnglesDown,
    faAnglesUp,
    faBook, faFile, faFileCode, faFileLines, faFolder,
    faFolderOpen,
    faGear, faReceipt, faSection
} from "@fortawesome/free-solid-svg-icons";
import {PlotSetup} from "../PlotSetup";
import {useAppDispatch, useAppSelector} from "../../../../../../../feature/tools";
import {DraftAct, DraftChapter, DraftScene, DraftSection, DraftStory} from "../../../../../../../feature/Draft";
import {faBookBlank} from "../../../../../../../components/icons/faBookEmptyRegular";
import {saveDraft} from "../../../../../../../feature/project/draftSlice";

export const PlotList = (
    props: {
        story: DraftStory,
        onItemSelected: (item: any) => void,
        data?: {
            data: any,
            type: 'act' | 'chapter' | 'scene' | 'story' | 'section'
        } | undefined
    }
) => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)
    const {projectId, storyId} = useParams();
    const user = useAppSelector(state => state.users.currentUser)
    const [expandedKeys, setExpandedKeys] = React.useState<string[]>(['story'])
    const [allKeys, setAllKeys] = React.useState<string[]>([])
    const [showSettings, setShowSettings] = React.useState(false)
    const [data, setData] = React.useState<{
        data: any,
        type: 'act' | 'chapter' | 'scene' | 'story' | 'section'
    } | undefined>(props.data)
    const storageProvider = useAppSelector(state => state.storageSlice.storageProvider)

    useEffect(() => {
        let actsKeys = props.story.acts.flatMap((act, index) => `act-${index + 1}`)
        let chaptersKeys = props.story.acts.flatMap((act, index) => act.chapters?.map((chapter, indexc) => `act-${index + 1}-chapter-${indexc + 1}`) ?? [])
        let sectionsKeys = props.story.acts.flatMap((act, index) => act.chapters?.flatMap((chapter, indexc) => chapter.sections?.map((s, indexs) => `act-${index + 1}-chapter-${indexc + 1}-section-${indexs + 1}`) ?? []) ?? [])
        let scenesKeys = props.story.acts.flatMap((act, index) => act.chapters?.flatMap((chapter, indexc) => chapter.sections?.flatMap((s, indexs) => s.scenes?.map((scene, indexsc) => `act-${index + 1}-chapter-${indexc + 1}-section-${indexs + 1}-scene-${indexsc + 1}`) ?? []) ?? []) ?? [])
        setAllKeys(actsKeys.concat(chaptersKeys).concat(sectionsKeys).concat(scenesKeys).concat('story'))

        if (data?.type == 'story') {
            props.onItemSelected({data: props.story, type: 'story'})
        }
        if (data?.type == 'act') {
            let acts = props.story.acts
            let act = {
                data: acts.find(act => act.id == data.data.id),
                type: 'act'
            }
            props.onItemSelected(act)
        }
        if (data?.type == 'chapter') {
            let chapters = props.story.acts.flatMap(act => act.chapters)
            let chapter = {
                data: chapters.find(chapter => chapter.id == data.data.id),
                type: 'chapter'
            }
            props.onItemSelected(chapter)
        }
        if (data?.type == 'section') {
            let sections = props.story.acts.flatMap(act => act.chapters).flatMap(chapter => chapter.sections)
            let section = {
                data: sections.find(section => section.id == data.data.id),
                type: 'section'
            }
            props.onItemSelected(section)
        }
        if (data?.type == 'scene') {
            let scenes = props.story.acts.flatMap(act => act.chapters).flatMap(chapter => chapter.sections).flatMap(section => section.scenes)
            let scene = {
                data: scenes.find(scene => scene.id == data.data.id),
                type: 'scene'
            }
            props.onItemSelected(scene)
        }
    }, [props.story]);

    useEffect(() => {
        setData(props.data)
    }, [props.data]);

    const hasShotsInStory = (story: DraftStory) => {
        return story.acts
            && story.acts.length > 0
            && story.acts[0].chapters
            && story.acts[0].chapters.length > 0
            && story.acts[0].chapters[0].sections
            && story.acts[0].chapters[0].sections.length > 0
            && story.acts[0].chapters[0].sections[0].scenes
            && story.acts[0].chapters[0].sections[0].scenes.length > 0
            && story.acts[0].chapters[0].sections[0].scenes[0].shots
            && story.acts[0].chapters[0].sections[0].scenes[0].shots?.length > 0
    }
    const hasShotsInAct = (act: DraftAct) => {
        return act.chapters
            && act.chapters.length > 0
            && act.chapters[0].sections
            && act.chapters[0].sections.length > 0
            && act.chapters[0].sections[0].scenes
            && act.chapters[0].sections[0].scenes.length > 0
            && act.chapters[0].sections[0].scenes[0].shots
            && act.chapters[0].sections[0].scenes[0].shots?.length > 0
    }
    const hasShotsInChapter = (chapter: DraftChapter) => {
        return chapter.sections
            && chapter.sections.length > 0
            && chapter.sections[0].scenes
            && chapter.sections[0].scenes.length > 0
            && chapter.sections[0].scenes[0].shots
            && chapter.sections[0].scenes[0].shots?.length > 0
    }
    const hasShotsInSection = (section: DraftSection) => {
        return section.scenes
            && section.scenes.length > 0
            && section.scenes[0].shots
            && section.scenes[0].shots?.length > 0
    }
    const hasShotsInScene = (scene: DraftScene) => {
        return scene.shots
            && scene.shots?.length > 0
    }

    const getState = (data: any, type: 'story' | 'act' | 'chapter' | 'scene' | 'section') => {
        switch (type) {
            case 'story':
                if (!data.acts || data.acts.length == 0) return false
                return data.acts?.filter((act: any) => getState(act, 'act') == true).length == data.acts.length ? true : undefined
            case 'act':
                if (!data.chapters || data.chapters.length == 0) return false
                return data.chapters.filter((chapter: any) => getState(chapter, 'chapter') == true).length == data.chapters.length ? true : undefined
            case 'chapter':
                if (!data.sections || data.sections.length == 0) return false
                return data.sections.filter((section: any) => getState(section, 'section') == true).length == data.sections.length ? true : undefined
            case 'section':
                if (!data.scenes || data.scenes.length == 0) return false
                return data.scenes.filter((scene: any) => getState(scene, 'scene') == true).length == data.scenes.length ? true : undefined
            case 'scene':
                return data.shots != undefined && data.shots.length > 0
        }
    }

    return (
        <Flex gap={10} vertical={true} style={{width: 350, minWidth: 350, height: "100%"}}>
            <Flex justify={'end'} gap={10}>
                <Tooltip title={'Expand all'}>
                    <Button
                        icon={<FontAwesomeIcon icon={faAnglesDown}/>}
                        disabled={allKeys.length == expandedKeys.length}
                        onClick={() => setExpandedKeys(allKeys)}
                    />
                </Tooltip>
                <Tooltip title={'Collapse all'}>
                    <Button
                        icon={<FontAwesomeIcon icon={faAnglesUp}/>}
                        disabled={expandedKeys.length == 0}
                        onClick={() => setExpandedKeys([])}
                    />
                </Tooltip>
                <Tooltip title={'Plot setup'}>
                    <Button
                        onClick={() => setShowSettings(true)}
                        icon={<FontAwesomeIcon icon={faGear}/>}>
                    </Button>
                </Tooltip>
            </Flex>
            <Flex style={{flexGrow: 1, overflowY: 'auto'}} vertical={true} gap={10}>

                <Flex
                    vertical={true} gap={10}
                    key={'story'}>
                    <Button
                        style={{
                            textAlign: 'start',
                            height: "auto",
                            background: data?.data ? "#f0f0f0" : undefined,
                            padding: '8px 8px 8px 16px'
                        }}
                        onDoubleClick={() => {
                            if (expandedKeys.includes('story')) {
                                setExpandedKeys([])
                            } else {
                                setExpandedKeys([...expandedKeys, 'story'])
                            }
                        }}
                        onClick={() => {
                            props.onItemSelected({data: props.story, type: 'story'})

                            if (hasShotsInStory(props.story)) {
                                scrollTo(`shot-${props.story.acts[0]?.chapters[0]?.sections[0]?.scenes[0]?.shots[0]?.id || ''}`)
                            }
                        }}

                        type={'text'}
                    >{(() => {
                        const state = getState(props.story, 'story')
                        return <Flex
                            style={{width: "100%"}}
                            justify={'space-between'}
                            gap={10}
                            align={'center'}>
                            <FontAwesomeIcon
                                style={{color: state == true ? "#0958d9" : state == false ? "#cccccc" : '#faad14'}}
                                fontSize={16}
                                icon={
                                    state == false ? faBookBlank : faBook
                                }/>
                            <div style={{
                                flexGrow: 1,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden'
                            }}>{props.story.title || 'Untitled'}</div>
                            <FontAwesomeIcon
                                style={{
                                    background: "#ffffff",
                                    border: "1px solid #eee",
                                    borderRadius: 20,
                                    padding: 5,
                                    aspectRatio: 1
                                }}
                                icon={expandedKeys.includes('story') ? faAngleDown : faAngleRight}
                                onClick={(e) => {
                                    if (expandedKeys.includes('story')) {
                                        setExpandedKeys(expandedKeys.filter(key => key !== 'story'))
                                    } else {
                                        setExpandedKeys([...expandedKeys, 'story'])
                                    }
                                    e.stopPropagation()
                                }}/>
                        </Flex>
                    })()}
                    </Button>
                    <Flex style={{marginLeft: 20}} vertical={true} gap={10}>
                        {expandedKeys.includes('story') ? props.story.acts?.map((act, index) => {
                                let active = data?.type == 'act' && data?.data.id == act.id || data?.type == 'chapter' && act.chapters?.map((item) => item.id).includes(data?.data.id) || data?.type == 'section' && act.chapters?.flatMap(chapter => chapter.sections?.map((item) => item.id) ?? []).includes(data?.data.id) || data?.type == 'scene' && act.chapters?.flatMap(chapter => chapter.sections.flatMap(s => s.scenes)?.map((item) => item.id) ?? []).includes(data?.data.id)
                                let actKey = `act-${index + 1}`
                                let chapters = act.chapters
                                let actState = getState(act, 'act')
                                return <Flex
                                    vertical={true} gap={10}
                                    key={actKey}
                                >
                                    <Button
                                        style={{
                                            textAlign: 'start',
                                            height: "auto",
                                            background: active ? "#f0f0f0" : undefined,
                                            padding: '8px 8px 8px 16px'
                                        }}
                                        onDoubleClick={() => {
                                            if (expandedKeys.includes(actKey)) {
                                                let allKeys = [actKey].concat(act.chapters?.map((chapter, indexc) => `act-${index + 1}-chapter-${indexc + 1}`) ?? [])
                                                setExpandedKeys(expandedKeys.filter(key => !allKeys.includes(key)))
                                            } else {
                                                setExpandedKeys([...expandedKeys, actKey])
                                            }
                                        }}
                                        onClick={() => {
                                            props.onItemSelected({data: act, type: 'act'})
                                            if (hasShotsInAct(act)) {
                                                scrollTo(`shot-${act.chapters[0]?.sections[0]?.scenes[0]?.shots[0]?.id || ''}`)
                                            }
                                        }}

                                        type={'text'}
                                    >
                                        <Flex
                                            style={{width: "100%"}}
                                            justify={'space-between'}
                                            gap={10}
                                            align={'center'}
                                        >
                                            <FontAwesomeIcon
                                                fontSize={16}
                                                style={{color: actState == true ? "#0958d9" : actState == false ? "#cccccc" : '#faad14'}}
                                                icon={expandedKeys.includes(actKey) ? faFolderOpen : faFolder}/>
                                            <div
                                                style={{
                                                    flexGrow: 1,
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {index + 1}. {act.title}
                                            </div>
                                            <FontAwesomeIcon
                                                style={{
                                                    background: "#ffffff",
                                                    border: "1px solid #eee",
                                                    borderRadius: 20,
                                                    padding: 5,
                                                    aspectRatio: 1
                                                }}
                                                icon={expandedKeys.includes(actKey) ? faAngleDown : faAngleRight}
                                                onClick={(e) => {
                                                    if (expandedKeys.includes(actKey)) {
                                                        setExpandedKeys(expandedKeys.filter(key => key !== actKey))
                                                    } else {
                                                        setExpandedKeys([...expandedKeys, actKey])
                                                    }
                                                    e.stopPropagation()
                                                }}/>
                                        </Flex>
                                    </Button>
                                    <Flex
                                        style={{marginLeft: 20}}
                                        vertical={true}
                                        gap={10}>
                                        {
                                            expandedKeys.includes(actKey) ? chapters.length > 0 ? chapters?.map((chapter, indexc) => {
                                                    let chapterKey = `act-${index + 1}-chapter-${indexc + 1}`
                                                    let active = data?.type == 'chapter' && data?.data.id == chapter.id || data?.type == 'section' && chapter.sections?.map((item) => item.id).includes(data?.data.id) || data?.type == 'scene' && chapter.sections.flatMap(s => s.scenes)?.map((item) => item.id).includes(data?.data.id)
                                                    let chapterState = getState(chapter, 'chapter')
                                                    return <>
                                                        <Button
                                                            type={'text'}
                                                            key={chapterKey}
                                                            style={{
                                                                textAlign: 'start',
                                                                height: "auto",
                                                                background: active ? "#f0f0f0" : undefined,
                                                                padding: '8px 8px 8px 16px'
                                                            }}
                                                            onDoubleClick={() => {
                                                                if (expandedKeys.includes(chapterKey)) {
                                                                    setExpandedKeys(expandedKeys.filter(key => key !== chapterKey))
                                                                } else {
                                                                    setExpandedKeys([...expandedKeys, chapterKey])
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                props.onItemSelected({
                                                                    data: chapter,
                                                                    type: 'chapter'
                                                                })
                                                                if (hasShotsInChapter(chapter)) {
                                                                    scrollTo(`shot-${chapter.sections[0].scenes[0]?.shots[0]?.id || ''}`)
                                                                }
                                                            }}>
                                                            <Flex
                                                                style={{width: "100%"}}
                                                                justify={'space-between'}
                                                                gap={10}
                                                                align={'center'}
                                                            >
                                                                <FontAwesomeIcon
                                                                    fontSize={16}
                                                                    style={{color: chapterState == true ? "#0958d9" : chapterState == false ? "#cccccc" : '#faad14'}}
                                                                    icon={chapterState == false ? faFile : faFileLines}
                                                                />
                                                                <div
                                                                    style={{
                                                                        flexGrow: 1,
                                                                        textOverflow: 'ellipsis',
                                                                        overflow: 'hidden'
                                                                    }}
                                                                >
                                                                    {indexc + 1}. {chapter.title}
                                                                </div>
                                                                <FontAwesomeIcon
                                                                    style={{
                                                                        background: "#ffffff",
                                                                        border: "1px solid #eee",
                                                                        borderRadius: 20,
                                                                        padding: 5,
                                                                        aspectRatio: 1
                                                                    }}
                                                                    icon={expandedKeys.includes(chapterKey) ? faAngleDown : faAngleRight}
                                                                    onClick={(e) => {
                                                                        if (expandedKeys.includes(chapterKey)) {
                                                                            setExpandedKeys(expandedKeys.filter(key => key !== chapterKey))
                                                                        } else {
                                                                            setExpandedKeys([...expandedKeys, chapterKey])
                                                                        }
                                                                        e.stopPropagation()
                                                                    }}/>
                                                            </Flex>
                                                        </Button>
                                                        <Flex
                                                            style={{marginLeft: 20}}
                                                            vertical={true}
                                                            gap={10}>
                                                            {
                                                                expandedKeys.includes(chapterKey) ? chapter.sections?.length > 0 ? chapter.sections?.map((section, indexs) => {
                                                                        let sectionKey = `act-${index + 1}-chapter-${indexc + 1}-section-${indexs + 1}`
                                                                        let active = data?.type == 'section' && data?.data.id == section.id || data?.type == 'scene' && section.scenes.map((item) => item.id).includes(data?.data.id)
                                                                        let sectionState = getState(section, 'section')
                                                                        return <>
                                                                            <Button
                                                                                type={'text'}
                                                                                key={sectionKey}
                                                                                style={{
                                                                                    textAlign: 'start',
                                                                                    height: "auto",
                                                                                    background: active ? "#f0f0f0" : undefined,
                                                                                    padding: '8px 8px 8px 16px'
                                                                                }}
                                                                                onDoubleClick={() => {
                                                                                    if (expandedKeys.includes(sectionKey)) {
                                                                                        setExpandedKeys(expandedKeys.filter(key => key !== sectionKey))
                                                                                    } else {
                                                                                        setExpandedKeys([...expandedKeys, sectionKey])
                                                                                    }
                                                                                }}
                                                                                onClick={() => {
                                                                                    props.onItemSelected({
                                                                                        data: section,
                                                                                        type: 'section'
                                                                                    })
                                                                                    if (hasShotsInSection(section)) {
                                                                                        scrollTo(`shot-${chapter.sections[0].scenes[0]?.shots[0]?.id || ''}`)
                                                                                    }
                                                                                }}>
                                                                                <Flex
                                                                                    style={{width: "100%"}}
                                                                                    justify={'space-between'}
                                                                                    gap={10}
                                                                                    align={'center'}
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        fontSize={16}
                                                                                        style={{color: sectionState == true ? "#0958d9" : sectionState == false ? "#cccccc" : '#faad14'}}
                                                                                        icon={sectionState == false ? faFile : faFileCode}
                                                                                    />
                                                                                    <div
                                                                                        style={{
                                                                                            flexGrow: 1,
                                                                                            textOverflow: 'ellipsis',
                                                                                            overflow: 'hidden'
                                                                                        }}
                                                                                    >
                                                                                        {indexs + 1}. {section.title}
                                                                                    </div>
                                                                                    <FontAwesomeIcon
                                                                                        style={{
                                                                                            background: "#ffffff",
                                                                                            border: "1px solid #eee",
                                                                                            borderRadius: 20,
                                                                                            padding: 5,
                                                                                            aspectRatio: 1
                                                                                        }}
                                                                                        icon={expandedKeys.includes(sectionKey) ? faAngleDown : faAngleRight}
                                                                                        onClick={(e) => {
                                                                                            if (expandedKeys.includes(sectionKey)) {
                                                                                                setExpandedKeys(expandedKeys.filter(key => key !== sectionKey))
                                                                                            } else {
                                                                                                setExpandedKeys([...expandedKeys, sectionKey])
                                                                                            }
                                                                                            e.stopPropagation()
                                                                                        }}/>
                                                                                </Flex>
                                                                            </Button>
                                                                            <Flex
                                                                                style={{marginLeft: 20}}
                                                                                vertical={true}
                                                                                gap={10}>
                                                                                {
                                                                                    expandedKeys.includes(sectionKey) ? section.scenes?.length > 0 ? section.scenes?.map((scene, indexsc) => {
                                                                                            let sceneKey = `act-${index + 1}-chapter-${indexc + 1}-section-${indexs + 1}-scene-${indexsc + 1}`
                                                                                            let active = data?.type == 'scene' && data?.data.id == scene.id
                                                                                            const sceneState = getState(scene, 'scene')
                                                                                            return <Button
                                                                                                style={{
                                                                                                    textAlign: 'start',
                                                                                                    height: "auto",
                                                                                                    background: active ? "#f0f0f0" : undefined,
                                                                                                    padding: '8px 8px 8px 16px'
                                                                                                }}
                                                                                                key={sceneKey} type={'text'}
                                                                                                onClick={() => {
                                                                                                    props.onItemSelected({
                                                                                                        data: scene,
                                                                                                        type: 'scene'
                                                                                                    })
                                                                                                    if (hasShotsInScene(scene)) {
                                                                                                        scrollTo(`shot-${scene.shots[0]?.id || ''}`)
                                                                                                    }
                                                                                                }}>
                                                                                                <Flex
                                                                                                    style={{width: "100%"}}
                                                                                                    justify={'space-between'}
                                                                                                    gap={10}
                                                                                                    align={'center'}>
                                                                                                    <FontAwesomeIcon fontSize={16}
                                                                                                                     style={{color: sceneState == true ? "#0958d9" : '#ccc'}}
                                                                                                                     icon={faReceipt}/>
                                                                                                    <div style={{
                                                                                                        flexGrow: 1,
                                                                                                        textOverflow: 'ellipsis',
                                                                                                        overflow: 'hidden'
                                                                                                    }}>{indexsc + 1}. {scene.title}</div>

                                                                                                </Flex>
                                                                                            </Button>
                                                                                        }
                                                                                    ) : <Button style={{textAlign: 'start'}}
                                                                                                disabled={true}
                                                                                                type={'text'}>
                                                                                        No scenes added yet
                                                                                    </Button> : ''
                                                                                }
                                                                            </Flex>
                                                                        </>
                                                                    }) :
                                                                    <Button style={{textAlign: 'start'}} disabled={true}
                                                                            type={'text'}>
                                                                        No sections added yet
                                                                    </Button> : ''}
                                                        </Flex>

                                                    </>
                                                }
                                            ) : <Button style={{textAlign: 'start'}} disabled={true} type={'text'}>
                                                No chapters added yet
                                            </Button> : ''}
                                    </Flex>
                                </Flex>
                            }
                        ) : ''}
                    </Flex></Flex>


            </Flex>
            {
                showSettings ?
                    <PlotSetup
                        onCancel={() => setShowSettings(false)}
                        onSave={() => {
                            if (storyId) {
                                dispatch(saveDraft({
                                    storageProvider: storageProvider,
                                    draft: draft,
                                    path: user?.id + "/" + projectId + "/" + storyId + "/",
                                }))
                            }
                            setShowSettings(false)
                        }}/> : ''
            }
        </Flex>

    )
}

const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start', inline: "start"});
}